import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listConfigs,
  createConfig,
  deleteConfig,
  type ChannelConfig,
} from "../api/config";
import {
  listWorkspaces,
  disconnectWorkspace,
  slackInstallUrl,
  workspaceImpact,
  type Workspace,
  type WorkspaceImpact,
} from "../api/workspaces";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { SlackBanner } from "../components/SlackBanner";
import { DisconnectConfirmModal } from "../components/DisconnectConfirmModal";

function formatConnectedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const emptyForm = {
  workspaceId: "",
  sourceChannelId: "",
  targetChannelId: "",
  timezone: "Europe/Bucharest",
  runAtHourLocal: 18,
  enabled: true,
};

export default function Settings() {
  const [rows, setRows] = useState<ChannelConfig[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalWs, setModalWs] = useState<Workspace | null>(null);
  const [impact, setImpact] = useState<WorkspaceImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [disconnectErr, setDisconnectErr] = useState<string | null>(null);

  // OAuth callback lands back here with ?slack=connected|error
  const slackResult = searchParams.get("slack");
  const slackTeam = searchParams.get("team");
  const slackReason = searchParams.get("reason");

  const activeWorkspaces = useMemo(
    () => workspaces.filter((w) => w.status === "active"),
    [workspaces],
  );

  async function refresh() {
    const [configs, ws] = await Promise.all([listConfigs(), listWorkspaces()]);
    setRows(configs);
    setWorkspaces(ws);
  }
  useEffect(() => {
    void refresh();
  }, []);

  // Default the form's workspace to the only active one.
  useEffect(() => {
    const only = activeWorkspaces.length === 1 ? activeWorkspaces[0] : undefined;
    if (!form.workspaceId && only) {
      setForm((f) => ({ ...f, workspaceId: only.id }));
    }
  }, [activeWorkspaces, form.workspaceId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createConfig(form);
      setForm({ ...emptyForm, workspaceId: form.workspaceId });
      await refresh();
    } catch (e) {
      setErr("Create failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await deleteConfig(id);
    await refresh();
  }

  async function openDisconnect(w: Workspace) {
    setModalWs(w); setImpact(null); setImpactLoading(true); setDisconnectErr(null);
    try {
      const i = await workspaceImpact(w.id);
      setImpact(i);
    } catch {
      setImpact({ configs: 0, reports: 0 });
    } finally { setImpactLoading(false); }
  }

  async function confirmDisconnect() {
    if (!modalWs) return;
    setDisconnectErr(null);
    try {
      await disconnectWorkspace(modalWs.id);
      setModalWs(null);
      await refresh();
    } catch { setDisconnectErr("Disconnect failed. Please retry."); }
  }

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      <SlackBanner
        slackResult={slackResult}
        slackTeam={slackTeam}
        slackReason={slackReason}
        onTryAgain={() => (window.location.href = slackInstallUrl())}
        onDismiss={() => setSearchParams({}, { replace: true })}
      />

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold">Slack workspaces</h2>
          <Button onClick={() => (window.location.href = slackInstallUrl())}>
            + Connect a workspace
          </Button>
        </div>
        {workspaces.length === 0 ? (
          <div className="border border-dashed border-border-default rounded-xl p-8 text-center">
            <p className="text-sm text-text-muted mb-4">
              No workspaces connected yet.<br/>Connect one to start receiving daily reports.
            </p>
            <Button onClick={() => (window.location.href = slackInstallUrl())}>
              Connect a workspace {"->"}            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {workspaces.map((w) => (
              <div key={w.id} className="border border-border-subtle rounded-xl p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={w.status === "active" ? "text-ok" : "text-danger-text"}>●</span>
                    <span className="font-semibold">{w.teamName}</span>
                  </div>
                  <div className="text-xs text-text-muted">
                    {w.slackTeamId} · connected {formatConnectedAt(w.createdAt)}
                  </div>
                  <div className="text-xs text-text-muted">
                    Bot user @{w.botUserId} · installed by {w.installedByUserId}
                  </div>
                </div>
                {w.status === "active" ? (
                  <button onClick={() => openDisconnect(w)} className="text-xs text-danger-text hover:underline">
                    Disconnect
                  </button>
                ) : (
                  <span className="text-xs text-danger-text">needs reconnect</span>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-text-muted mt-4">
          (i) Haven't invited the bot yet? Run{" "}
          <span className="font-mono">/invite @daybrief</span> in each private channel
          you want reports on.
        </p>
      </Card>

      <Card>
        <h2 className="font-display text-lg font-semibold mb-3">Channel configs</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-text-muted">No configs yet.</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="text-text-muted uppercase tracking-widest text-[10.5px]">
              <tr>
                <th className="text-left py-2">Workspace</th>
                <th className="text-left py-2">Source</th>
                <th className="text-left py-2">Target</th>
                <th className="text-left py-2">TZ</th>
                <th className="text-left py-2">Hour</th>
                <th className="text-left py-2">Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border-subtle">
                  <td className="py-2">{r.teamName}</td>
                  <td className="py-2 font-mono">{r.sourceChannelId}</td>
                  <td className="py-2 font-mono">{r.targetChannelId}</td>
                  <td className="py-2">{r.timezone}</td>
                  <td className="py-2">{r.runAtHourLocal}:00</td>
                  <td className="py-2">{r.enabled ? "on" : "off"}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => remove(r.id)}
                      className="text-[12px] text-danger-text hover:underline"
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h2 className="font-display text-lg font-semibold mb-3">Add config</h2>
        {activeWorkspaces.length === 0 ? (
          <p className="text-sm text-text-muted">
            Connect a Slack workspace first — configs belong to a workspace.
          </p>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <label className="flex flex-col text-xs text-text-secondary col-span-2">
              Workspace
              <select
                required
                value={form.workspaceId}
                onChange={(e) => setForm({ ...form, workspaceId: e.target.value })}
                className="mt-1 px-3 py-2 text-sm rounded-md border border-border-default bg-transparent"
              >
                <option value="" disabled>
                  Select a workspace…
                </option>
                {activeWorkspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.teamName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-xs text-text-secondary">
              Source channel ID
              <input required value={form.sourceChannelId}
                onChange={(e) => setForm({ ...form, sourceChannelId: e.target.value })}
                className="mt-1 px-3 py-2 text-sm rounded-md border border-border-default" />
            </label>
            <label className="flex flex-col text-xs text-text-secondary">
              Target channel ID
              <input required value={form.targetChannelId}
                onChange={(e) => setForm({ ...form, targetChannelId: e.target.value })}
                className="mt-1 px-3 py-2 text-sm rounded-md border border-border-default" />
            </label>
            <label className="flex flex-col text-xs text-text-secondary">
              Timezone
              <input required value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                className="mt-1 px-3 py-2 text-sm rounded-md border border-border-default" />
            </label>
            <label className="flex flex-col text-xs text-text-secondary">
              Run at hour (0-23)
              <input required type="number" min={0} max={23} value={form.runAtHourLocal}
                onChange={(e) => setForm({ ...form, runAtHourLocal: Number(e.target.value) })}
                className="mt-1 px-3 py-2 text-sm rounded-md border border-border-default" />
            </label>
            <label className="flex items-center gap-2 text-xs text-text-secondary">
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>
            {err && (
              <div className="text-xs text-danger-text col-span-2">{err}</div>
            )}
            <div className="col-span-2">
              <Button type="submit" disabled={busy}>
                {busy ? "Creating…" : "Create"}
              </Button>
            </div>
          </form>
        )}
      </Card>
      <DisconnectConfirmModal
        open={!!modalWs}
        workspaceName={modalWs?.teamName ?? ""}
        impact={impact}
        loading={impactLoading}
        error={disconnectErr}
        onCancel={() => setModalWs(null)}
        onConfirm={confirmDisconnect}
      />
    </div>
  );
}
